import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { services } from '../data/mock';
import { ArrowLeft, Check, Mail } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = services.find(s => s.slug === slug);

  // Email selon le service
  const getServiceEmail = (slug) => {
    if (slug === 'beats-production') return 'Beats@GailLleyton.com';
    return 'Services@GailLleyton.com';
  };

  const serviceEmail = service ? getServiceEmail(service.slug) : 'contact@gailleyton.com';

  if (!service) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl text-white mb-4">Service non trouvé</h1>
          <Link to="/" className="text-rose-400 hover:text-rose-300">
                Retour à l'accueil
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black min-h-screen">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-neutral-900 to-black">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft size={20} />
            Retour
          </Link>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-rose-800 to-violet-800 rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-500"></div>
              <img
                src={service.image}
                alt={service.title}
                className="relative rounded-2xl w-full h-96 object-cover shadow-2xl"
              />
            </div>

            {/* Content */}
            <div>
              <h1 className="font-display text-5xl md:text-6xl font-bold uppercase text-white mb-6 tracking-wide">
                {service.title}
              </h1>
              <p className="text-gray-300 text-xl leading-relaxed mb-8">
                {service.fullDescription}
              </p>
              <a
                href={`mailto:${serviceEmail}?subject=${encodeURIComponent('Demande: ' + service.title)}`}
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-rose-800 to-violet-800 text-white rounded-full font-semibold uppercase text-sm tracking-wider hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-rose-500/50"
              >
                <Mail size={20} />
                Me Contacter
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <h2 className="font-display text-4xl font-bold uppercase text-white mb-12 text-center tracking-wide">
            Ce qui est inclus
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {service.features.map((feature, index) => (
              <div
                key={index}
                className="bg-neutral-900 border border-neutral-800 rounded-xl p-6 hover:border-rose-500/50 transition-all duration-300"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-rose-800 to-violet-800 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check size={20} className="text-white" />
                  </div>
                  <span className="text-gray-200 font-medium">{feature}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-gradient-to-b from-black to-neutral-900">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <h2 className="font-display text-4xl font-bold uppercase text-white mb-4 text-center tracking-wide">
            Tarifs
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Choisissez la formule qui correspond à vos besoins
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {service.pricing.map((plan, index) => (
              <div
                key={index}
                className={`relative bg-gradient-to-br from-neutral-900 to-neutral-800 border rounded-2xl p-8 hover:transform hover:-translate-y-2 transition-all duration-300 ${
                  plan.popular 
                    ? 'border-rose-500 ring-2 ring-rose-500/50' 
                    : 'border-neutral-700 hover:border-rose-500/50'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-rose-800 to-violet-800 text-white rounded-full text-xs uppercase tracking-wider font-semibold shadow-lg">
                    Populaire
                  </div>
                )}

                <h3 className="text-2xl font-bold text-white mb-4">{plan.name}</h3>
                <div className="text-4xl font-bold text-white mb-6">{plan.price}</div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-300">
                      <Check size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={`mailto:${serviceEmail}?subject=${encodeURIComponent('Réservation: ' + plan.name + ' - ' + service.title)}`}
                  className={`block w-full text-center px-6 py-3 rounded-full font-medium uppercase text-sm tracking-wider transition-all duration-300 ${
                    plan.popular
                      ? 'bg-gradient-to-r from-rose-800 to-violet-800 text-white hover:scale-105 shadow-lg hover:shadow-rose-500/50'
                      : 'bg-neutral-800 text-white hover:bg-neutral-700'
                  }`}
                >
                  Réserver
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-neutral-900">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Prêt à démarrer votre projet ?
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Contactez-moi pour discuter de vos besoins et obtenir un devis personnalisé
          </p>
          <a
            href={`mailto:${serviceEmail}?subject=${encodeURIComponent('Demande de devis: ' + service.title)}`}
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black rounded-full font-bold uppercase text-sm tracking-wider hover:scale-105 transition-transform duration-300"
          >
            <Mail size={20} />
            Demander un Devis
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceDetail;
